import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { toggleMenu, closeMenu, closeScanner } from '../store/slices/navigationSlice';
import logo from '../assets/images/logo-time-capsule.png';
import { Menu, X } from 'lucide-react';
import PersistenceTest from './PersistenceTest';
import LanguageSwitcher from './LanguageSwitcher';
import FullscreenButton from './FullscreenButton';
import IOSPWADemo from './IOSPWADemo';
import IOSPWADebugger from './IOSPWADebugger';

import QrScannerModal from './QrScannerModal';
import { useFullscreen } from '../hooks/useFullscreen';
import { useOrientation } from '../hooks/useDeviceDetection';
import { useIOSPWADetection } from '../hooks/useIOSPWADetection';
import { useScrollDirection } from '../hooks/useScrollDirection';
import { APP_ENVIRONMENT, APP_VERSION, SHOW_IOS_PWA_DEBUGGER } from '../utils/constants';
import { isAndroidPWA } from '../utils/functions';

export default function Layout({ children }: { children: React.ReactNode }) {
    const { isSupported } = useFullscreen();
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const location = useLocation();
    const { isMenuOpen, isScannerOpen } = useAppSelector(state => state.navigation);
    const { visitorId, language, name, nationality } = useAppSelector(state => state.user);
    const { currentExhibit } = useAppSelector(state => state.exhibit);
    const orientation = useOrientation();
    const { isIOSPWA } = useIOSPWADetection();
    const scrollingDown = useScrollDirection({ 
        threshold: 30 
    });

    const isLandscape = orientation === 'landscape';

    // Pages where the menu button should be hidden
    const hideMenuButtonPages = ['/language', '/privacy', '/register', '/selfie', '/ready-to-explore', '/leave-your-mark'];
    const shouldHideMenuButton = isLandscape || hideMenuButtonPages.includes(location.pathname);

    // Pages where the logo link navigation should be disabled
    const disableLinkNavigationPages = ['/language', '/privacy', '/register', '/selfie', '/ready-to-explore', '/leave-your-mark'];
    const shouldDisableLinkNavigation = disableLinkNavigationPages.includes(location.pathname);

    const navigationMenuItems = [
        { 
            id: 'orang-laut', 
            name: t('navigation.orangLaut')
        }, 
        { 
            id: 'market-traders', 
            name: t('navigation.marketTraders')
        }, 
        { 
            id: 'badang', 
            name: t('navigation.badang')
        }, 
        { 
            id: 'book-of-myths', 
            name: t('navigation.bookOfMyths')
        }, 
        { 
            id: 'sg60', 
            name: t('navigation.sg60')
        }, 
        { 
            id: 'peranakan-design', 
            name: t('navigation.peranakanDesign')
        }, 
        { 
            id: 'time-capsule', 
            name: t('navigation.timeCapsule')
        }
    ];
    
    const handleToggleMenu = () => {
        dispatch(toggleMenu());
    };
    
    const handleCloseMenu = () => {
        dispatch(closeMenu());
    };

    const handleCloseScannerModal = () => {
        dispatch(closeScanner());
    };

    return (
        <div className={`flex flex-col min-h-screen ${isIOSPWA ? 'ios-pwa-safe-horizontal ios-pwa-safe-bottom' : ''}`}>
            {<header className={`
                flex flex-col items-center 
                p-4 
                sticky top-0 has-transition ${(isLandscape || (scrollingDown && !shouldHideMenuButton)) ? '-translate-y-[112px] opacity-0' : 'translate-y-0 opacity-100'} 
                w-full 
                bg-black/40 backdrop-blur-lg
                z-50 
                isolate
                has-transition
                ${isIOSPWA ? 'ios-pwa-safe-top ios-pwa-safe-top-force' : ''}
            `}>
                {shouldDisableLinkNavigation ? (
                    <div className="text-lg font-bold cursor-default">
                        <img src={logo} alt="Time Capsule" className="h-20" />
                    </div>
                ) : (
                    <Link to={`${language ? "/" : "/language"}`} className="text-lg font-bold">
                        <img src={logo} alt="Time Capsule" className="h-20" />
                    </Link>
                )}
            </header>}

            {/* Fixed hamburger menu button */}
            {!shouldHideMenuButton && (
                <button 
                    onClick={handleToggleMenu} 
                    className={`
                        fixed has-transition 
                        ${scrollingDown 
                            ? ((isIOSPWA ? 'top-16' : 'top-5') + ' bg-black/40 backdrop-blur-lg') 
                            : (isIOSPWA ? 'top-20' : 'top-9')} 
                        right-4 text-xl p-2 z-50 rounded-md 
                        ${isIOSPWA ? 'ios-pwa-safe-top' : ''}
                        ${isAndroidPWA() ? (scrollingDown ? '!top-7' : '!top-12') : ''}
                    `}
                    aria-label={t('layout.toggleMenu')}
                >
                    <Menu size={24} />
                </button>
            )}

            <nav className={`slide-in-menu w-full sm:w-full md:w-96 lg:w-96 relative isolate ${isMenuOpen ? 'translate-x-10' : 'translate-x-full'}`}>
                <div className="flex gap-6 items-center p-4 sm:py-0 h-28">
                    <button 
                        onClick={handleCloseMenu} 
                        className="text-xl p-2"
                        aria-label={t('layout.closeMenu')}
                    >
                        <X size={24} />
                    </button>
                    <h2 className="text-xl 2xs:text-lg xs:text-xl font-medium uppercase">{t('layout.navigationMenu')}</h2>
                </div>

                {navigationMenuItems.length > 0 && 
                <ul className={`
                    flex flex-col gap-0 
                    py-4 px-16 
                    2xs:pt-0 xs:pt-0 sm:pt-0 
                    max-h-full  
                    2xs-landscape:!max-h-[60%] 2xs-landscape:!overflow-y-auto xs-landscape:max-h-[70%] 
                    xs:overflow-y-scroll 
                `}>
                    {navigationMenuItems.map(
                        (item) => <Link to={`/exhibit/${item.id}`} key={item.id} onClick={handleCloseMenu}>
                            <li className={`
                                slide-in-menu-list-item 
                                has-transition 
                                2xs-landscape:!py-[0.1rem] xs-landscape:py-0.5
                                ${currentExhibit === item.id ? 'highlighted' : ''}
                            `}>
                                {item.name}
                            </li>
                        </Link>
                    )}
                </ul>}

                {/* Language Switcher */}
                <div className="px-16 pb-4">
                    <LanguageSwitcher />
                </div>

                {/* FullscreenButton */}
                {isSupported && <div className="px-16 pb-4">
                    <FullscreenButton 
                        className="!border-[var(--color-light-gray)]"
                        showLabel={true}
                        enterFullscreenText={t('common.enterFullscreen')}
                        exitFullscreenText={t('common.exitFullscreen')}
                    />
                </div>}

                {/* Visitor ID */}
                {<div className="absolute bottom-0 right-0 px-16 pb-4 max-w-full text-sm text-white/50">
                    <div className="leading-tight outline-dashed outline-1 outline-white/50 rounded-xl px-3 py-2 my-2">
                        {APP_ENVIRONMENT === 'Dev' && <div>{`Name: ${name || '----'}`}</div>}
                        {APP_ENVIRONMENT === 'Dev' && <div>{`Nationality: ${nationality || '----'}`}</div>}
                        {<div className='border-dashed border-b border-white/50 pb-2 mb-2 overflow-x-auto'>{`Visitor ID: ${visitorId || '----'}`}</div>}

                        <div className="flex gap-2 justify-between">
                            {<div className="w-4/5 text-xs leading-4">{t('layout.demoDisclaimer')}</div>}
                            
                            {/* Version number */}
                            <div className="w-1/5 text-right content-end leading-none">
                                {`${APP_ENVIRONMENT === 'Development' ? '-- Prototype --: ' : APP_ENVIRONMENT === 'Staging' ? '' : '-- Dev --: '}v${APP_VERSION}`}
                            </div>
                        </div>
                    </div>
                </div>}
                
            </nav>

            <main className={`relative content-frame flex-grow isolate ${isIOSPWA ? 'ios-pwa-main-content ios-pwa-main-content-force' : 'p-0 m-0'}`}>{children}</main>
            
            {/* QR Scanner Modal */}
            <QrScannerModal 
                isOpen={isScannerOpen}
                onClose={handleCloseScannerModal}
                showCloseButton={false}
            />
            
            {/* Temporary test component - TODO: remove in production */}
            {process.env.NODE_ENV === 'development' && <PersistenceTest />}
            
            {/* iOS PWA Demo - TODO: remove in production */}
            {process.env.NODE_ENV === 'development' && <IOSPWADemo />}
            
            {/* iOS PWA Debugger - configurable via environment variable */}
            {SHOW_IOS_PWA_DEBUGGER && <IOSPWADebugger />}
        </div>
    );
}